import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Package,
  Search,
  Filter,
  Plus,
  Eye,
  Edit,
  Trash2,
  Download,
  Calendar,
  SortAsc,
  SortDesc,
  MessageSquare
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Progress } from './ui/progress';
import { orderService } from '../services/api';
import { formatCurrency, formatDate, formatQuantity } from '../mock';
import { useToast } from '../hooks/use-toast';

const OrdersList = ({ searchQuery = '' }) => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('All');
  const [sortField, setSortField] = useState('orderDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  // For now, let's use a simple admin check - in production this would come from auth service
  const currentUser = { role: 'Admin' }; // Temporary for testing
  const isAdmin = currentUser?.role === 'Admin';

  useEffect(() => {
    // Load orders from database
    const loadOrders = async () => {
      try {
        const allOrders = await orderService.getAllOrders();
        setOrders(allOrders);
      } catch (error) {
        console.error('Error loading orders:', error);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadOrders();
    
    // Check for status filter from URL
    const urlStatus = searchParams.get('status');
    if (urlStatus) {
      setStatusFilter(urlStatus);
    }
  }, [searchParams]);

  useEffect(() => {
    // Apply filters and search
    let filtered = [...orders];

    // Status filter
    if (statusFilter !== 'All') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(order => 
        order.brandName.toLowerCase().includes(query) ||
        order.poNumber.toLowerCase().includes(query) ||
        order.materialType.toLowerCase().includes(query) ||
        order.billTo.toLowerCase().includes(query) ||
        order.shipTo.toLowerCase().includes(query)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      if (sortField === 'orderDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredOrders(filtered);
  }, [orders, statusFilter, searchQuery, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExcelDownload = () => {
    if (!isAdmin) {
      toast({
        title: 'Access Denied',
        description: 'Only administrators can download orders data.',
        variant: 'destructive'
      });
      return;
    }

    const result = orderManager.downloadOrdersAsExcel();
    if (result.success) {
      toast({
        title: 'Download Started',
        description: result.message,
      });
    } else {
      toast({
        title: 'Download Failed',
        description: result.message,
        variant: 'destructive'
      });
    }
  };

  const handleDeleteOrder = async (orderNumber) => {
    if (window.confirm(`Are you sure you want to delete order ${orderNumber}? This action cannot be undone and the order number will not be reused.`)) {
      try {
        await orderService.deleteOrderByNumber(orderNumber);
        // Reload orders from database
        const allOrders = await orderService.getAllOrders();
        setOrders(allOrders);
        alert(`Order ${orderNumber} deleted successfully. This order number will not be reused.`);
      } catch (error) {
        console.error('Error deleting order:', error);
        alert(`Error deleting order: ${error.message}`);
      }
    }
  };

  const getStatusColor = (status) => {
    return status === 'Complete' 
      ? 'bg-green-100 text-green-800 border-green-200' 
      : 'bg-yellow-100 text-yellow-800 border-yellow-200';
  };

  const getProgressPercentage = (order) => {
    if (order.quantity === 0) return 0;
    const dispatched = order.quantity - order.balance;
    return Math.round((dispatched / order.quantity) * 100);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? 
      <SortAsc className="w-4 h-4 ml-1" /> : 
      <SortDesc className="w-4 h-4 ml-1" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Orders Management</h1>
            <p className="text-gray-600">
              {filteredOrders.length} of {orders.length} orders
              {searchQuery && ` matching "${searchQuery}"`}
            </p>
          </div>
          <div className="flex space-x-3">
            {isAdmin && (
              <Button 
                variant="outline" 
                className="flex items-center"
                onClick={handleExcelDownload}
              >
                <Download className="w-4 h-4 mr-2" />
                Download Excel
              </Button>
            )}
            <Link to="/add-order">
              <Button className="bg-blue-600 hover:bg-blue-700 flex items-center">
                <Plus className="w-4 h-4 mr-2" />
                Add Order
              </Button>
            </Link>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search orders by brand, PO number, material..."
                    value={searchQuery}
                    className="pl-10"
                    readOnly
                  />
                </div>
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-48">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Status</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Complete">Complete</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Orders Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Package className="w-5 h-5 mr-2" />
              Orders List
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {filteredOrders.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No orders found</h3>
                <p className="text-gray-600 mb-4">
                  {searchQuery ? 'Try adjusting your search terms' : 'Get started by adding your first order'}
                </p>
                <Link to="/add-order">
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Order
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('orderNumber')}
                      >
                        <div className="flex items-center">
                          Order Number
                          <SortIcon field="orderNumber" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('orderDate')}
                      >
                        <div className="flex items-center">
                          Date
                          <SortIcon field="orderDate" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('brandName')}
                      >
                        <div className="flex items-center">
                          Brand Name
                          <SortIcon field="brandName" />
                        </div>
                      </TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead className="text-right">Balance Quantity</TableHead>
                      <TableHead className="text-right">Price per unit</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredOrders.map((order) => (
                      <TableRow key={order.id} className="hover:bg-gray-50">
                        <TableCell>
                          <div className="font-medium text-blue-700">
                            {order.orderNumber || `ORD-${String(order.id).padStart(3, '0')}`}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {formatDate(order.orderDate)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <Link 
                              to={`/orders/${order.orderNumber}`}
                              className="font-medium text-blue-600 hover:text-blue-900 hover:underline cursor-pointer"
                            >
                              {order.brandName}
                            </Link>
                            {order.poNumber && (
                              <div className="text-sm text-gray-500">PO: {order.poNumber}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="font-medium">
                            {formatQuantity(order.quantity, order.unit)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="font-medium text-orange-600">
                            {formatQuantity(order.balance, order.unit)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="font-medium">
                            {formatCurrency(order.price)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(order.status)}>
                            {order.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end space-x-1">
                            <Link to={`/orders/${order.orderNumber}/dispatch`}>
                              <Button variant="outline" size="sm" title="Dispatch Management">
                                <Package className="w-4 h-4 mr-1" />
                                Dispatch
                              </Button>
                            </Link>
                            {order.brandId ? (
                              <Link to={`/brand-specs/view-only/brand/${order.brandId}`}>
                                <Button variant="outline" size="sm" title="View Brand Specifications">
                                  <Eye className="w-4 h-4 mr-1" />
                                  View Specs
                                </Button>
                              </Link>
                            ) : (
                              <Button variant="outline" size="sm" title="Brand specifications not available" disabled>
                                <Eye className="w-4 h-4 mr-1" />
                                View Specs
                              </Button>
                            )}
                            <Link to={`/orders/${order.orderNumber}/notes`}>
                              <Button variant="outline" size="sm" title="Order Notes">
                                <MessageSquare className="w-4 h-4 mr-1" />
                                Notes
                              </Button>
                            </Link>
                            {isAdmin && (
                              <>
                                <Link to={`/orders/${order.orderNumber}/edit`}>
                                  <Button variant="ghost" size="sm" title="Edit Order">
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                </Link>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleDeleteOrder(order.orderNumber)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  title="Delete Order"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OrdersList;